import { Badge } from 'core/components/ui/Badge'
import { Modal } from 'core/components/ui/Modal'
import React from 'react'
import { PAYMENT_MODE_LABELS, PaymentDetailRecord } from '../../domain/entities/Payment'

interface PaymentDetailModalProps {
  isOpen: boolean
  onClose: () => void
  record: PaymentDetailRecord | null
  loading?: boolean
  onApprove?: () => void
  onReject?: () => void
  readOnly?: boolean
}

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

const FieldIcon: React.FC<{ path: string }> = ({ path }) => (
  <svg className="h-3.5 w-3.5 text-text-secondary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path} />
  </svg>
)

const ICON_PATHS = {
  phone:
    'M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  mail: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  pin: 'M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
} as const

export const PaymentDetailModal: React.FC<PaymentDetailModalProps> = ({
  isOpen,
  onClose,
  record,
  loading = false,
  onApprove,
  onReject,
  readOnly = false,
}) => {
  if (!isOpen) return null

  const hasBuyerProfile = !!(record?.buyer?.name || record?.buyer?.role)
  const referenceLabel = record?.paymentMode === 'upi' ? 'UPI Txn Ref Code' : 'Payment Reference'
  const referenceMethodLabel = record ? PAYMENT_MODE_LABELS[record.paymentMode] : '—'

  const footerActions = !readOnly && (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:justify-end gap-3 w-full">
      <button
        onClick={onReject}
        className="px-4 py-2 text-sm font-semibold border border-danger/30 hover:border-danger text-danger rounded-btn bg-surface hover:bg-danger/[0.04] transition-colors w-full sm:w-auto"
      >
        Reject Payment
      </button>
      <button
        onClick={onApprove}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors shadow w-full sm:w-auto"
      >
        Verify & Confirm
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={record ? `Verify Invoice Payment - ${record.id}` : 'Verify Invoice Payment'}
      footer={record && !loading ? footerActions || undefined : undefined}
      size="xl"
    >
      {loading || !record ? (
        <div className="flex items-center justify-center py-16">
          <svg className="animate-spin h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            {/* Info Details */}
            <div className="space-y-4 sm:space-y-5">
              {hasBuyerProfile && (
                <div className="border border-border rounded-card bg-surface overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-bg/40">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 21h18M5 21V7l8-4v18M13 21V11l6 3v7M9 9v.01M9 12v.01M9 15v.01"
                          />
                        </svg>
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">
                          FPO / Retailer / VLCC
                        </p>
                        <p className="font-bold text-text-primary text-sm truncate">
                          {record.buyer?.businessName || record.buyer?.name || '—'}
                        </p>
                      </div>
                    </div>
                    {record.buyer?.role && (
                      <Badge tone="primary" variant="subtle" className="shrink-0 font-bold">
                        {record.buyer.role}
                      </Badge>
                    )}
                  </div>

                  {(record.buyer?.phone || record.buyer?.email || record.buyerAddress) && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {record.buyer?.phone && (
                          <div className="flex items-start gap-2 text-xs">
                            <FieldIcon path={ICON_PATHS.phone} />
                            <div className="min-w-0">
                              <span className="block text-text-secondary">Contact Number</span>
                              <span className="block font-semibold text-text-primary truncate">
                                {record.buyer.phone}
                              </span>
                            </div>
                          </div>
                        )}
                        {record.buyer?.email && (
                          <div className="flex items-start gap-2 text-xs">
                            <FieldIcon path={ICON_PATHS.mail} />
                            <div className="min-w-0">
                              <span className="block text-text-secondary">Email</span>
                              <span className="block font-medium text-text-primary truncate">{record.buyer.email}</span>
                            </div>
                          </div>
                        )}
                        {record.buyerAddress && (
                          <div className="flex items-start gap-2 text-xs sm:col-span-2">
                            <FieldIcon path={ICON_PATHS.pin} />
                            <div className="min-w-0">
                              <span className="block text-text-secondary">Address</span>
                              <span className="block font-semibold text-text-primary leading-normal">
                                {record.buyerAddress}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="border border-border rounded-card bg-surface overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-bg/40">
                  <span className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">
                    Transaction Details
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-text-secondary text-xs block">Linked Order ID</span>
                    <span className="font-semibold text-text-primary block truncate">{record.orderId}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary text-xs block">Amount Received</span>
                    <span className="font-bold text-primary block">{formatCurrency(record.amount)}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary text-xs block">Reference Method</span>
                    <span className="font-semibold text-text-primary block">{referenceMethodLabel}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary text-xs block">{referenceLabel}</span>
                    <span className="font-mono text-text-primary block truncate">{record.qrRef || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Screenshot zoom frame */}
            <div className="border border-border rounded-card overflow-hidden bg-bg/30 flex flex-col min-h-[16rem]">
              <div className="bg-bg border-b border-border px-4 py-3 flex justify-between items-center text-[11px] font-bold text-text-secondary uppercase tracking-wider">
                <span>Receipt Screenshot Attachment</span>
                {record.screenshotUrl && (
                  <a
                    href={record.screenshotUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline font-semibold normal-case tracking-normal"
                  >
                    View Full
                  </a>
                )}
              </div>
              {record.screenshotUrl ? (
                <div className="flex-1 overflow-hidden">
                  <img
                    src={record.screenshotUrl}
                    alt="Payment Receipt"
                    className="w-full h-full min-h-[16rem] max-h-[28rem] object-contain hover:scale-105 transition-transform duration-200"
                  />
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-xs text-text-secondary">
                  <svg
                    className="h-10 w-10 text-text-secondary mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  No screenshot uploaded. Payment logged manually as {referenceMethodLabel} by admin portal.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
